import { IconNames } from '@blueprintjs/icons';
import { DateFormats, formatDate } from '@strata/helper-functions';
import { ActionButtonPageTitle, IconButton } from '@strata/react-buttons';
import { ButtonWithDropdownMenu } from '@strata/react-dropdown-menu';
import { FormHeaderAndContent } from '@strata/react-form-header-and-content';
import { FormSectionWithRows } from '@strata/react-form-section';
import { DetailLabel, DetailValue, FormTitleLabelValuePair } from '@strata/react-label-value-pairs';
import { SuccessToast } from '@strata/react-toast-message';
import dayjs from 'dayjs';
import { type ReactNode, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import {
    type EditableFieldType,
    EditIncidentFieldModal,
} from 'components/incidentManagement/dialogs/EditIncidentFieldModal';
import { MarkAsResolvedDialog } from 'components/incidentManagement/dialogs/MarkAsResolvedDialog';
import { EditableFieldWithEditButton } from 'components/incidentManagement/EditableFieldWithEditButton';
import { handleError } from 'services/Error';
import { AppRoutes } from 'services/routes';
import {
    useEditIncidentMutation,
    useGetImpactedServiceNamesQuery,
    useGetIncidentQuery,
    useGetMissingQualityChecksQuery,
    useGetSeverityLevelsQuery,
} from 'store/api/oopsApi';

const OTHER_OPTION = 'Other';

const FIELD_LABELS: Record<EditableFieldType, string> = {
    name: 'Name',
    primaryImpactedServiceName: 'Primary Impacted Service',
    description: 'Description',
    startedAt: 'Started At',
    missingQualityCheck: 'Missing Quality Check',
    severity: 'Severity',
};

export const IncidentDetails = () => {
    const navigate = useNavigate();
    const { incidentId } = useParams<{ incidentId: string }>();
    const [isMarkAsResolvedDialogOpen, setIsMarkAsResolvedDialogOpen] = useState(false);
    const [editingField, setEditingField] = useState<EditableFieldType | null>(null);

    const [editIncident] = useEditIncidentMutation();
    const { data: incident, isFetching } = useGetIncidentQuery(incidentId!);

    const { data: missingQualityChecksFromApi = [] } = useGetMissingQualityChecksQuery();
    const missingQualityChecks = [...missingQualityChecksFromApi, OTHER_OPTION];

    const { data: impactedServiceNamesFromApi = [] } = useGetImpactedServiceNamesQuery();
    const impactedServiceNames = [...impactedServiceNamesFromApi, OTHER_OPTION];

    const { data: severityLevels = [] } = useGetSeverityLevelsQuery();
    const severityLevelNames = severityLevels
        .slice()
        .sort((a, b) => a.id - b.id)
        .map((severity) => severity.name);

    const handleSave = async (field: EditableFieldType, value: string | number | undefined) => {
        if (!incidentId || value === undefined) return;

        const payload: Record<string, string | number> = {};
        if (field === 'name') payload.name = value;
        if (field === 'description') payload.description = value;
        if (field === 'startedAt') {
            const date = dayjs(value);
            payload.started_at = date.isValid() ? date.toISOString() : '';
        }
        if (field === 'missingQualityCheck') payload.missing_quality_check = value ?? '';
        if (field === 'primaryImpactedServiceName')
            payload.primary_impacted_service_name = value ?? '';
        if (field === 'severity') payload.severity = value;

        if (Object.keys(payload).length === 0) return;

        try {
            await editIncident({ incidentId, ...payload }).unwrap();
            SuccessToast({ message: 'Incident updated successfully.' });
            setEditingField(null);
        } catch (err) {
            handleError('Unable to update incident. Please try again.', err as Error);
        }
    };

    const renderEditableDetailField = (field: EditableFieldType, value: string | undefined) => (
        <EditableFieldWithEditButton
            key={field}
            label={FIELD_LABELS[field]}
            value={value}
            onEditClick={() => setEditingField(field)}
        />
    );

    const renderReadOnlyDetailField = (label: string, value: string | undefined) => (
        <div key={label} style={{ width: '33%' }}>
            <DetailLabel label={label} />
            <DetailValue value={value} />
        </div>
    );

    const buildDetailRows = (): { rowOne: ReactNode[]; rowTwo?: ReactNode[] } => {
        if (!incident) {
            return { rowOne: [] };
        }

        const rowOne = [
            renderEditableDetailField('description', incident.description),
            renderEditableDetailField(
                'startedAt',
                formatDate(incident.startedAt, DateFormats.DATE_TIME),
            ),
            renderReadOnlyDetailField(
                'Resolved At',
                incident.resolvedAt
                    ? formatDate(incident.resolvedAt, DateFormats.DATE_TIME)
                    : undefined,
            ),
        ];

        const rowTwo = [
            renderEditableDetailField(
                'primaryImpactedServiceName',
                incident.primaryImpactedServiceName,
            ),
            renderEditableDetailField('missingQualityCheck', incident.missingQualityCheck),
            renderEditableDetailField(
                'severity',
                severityLevels.find((severity) => severity.id === incident.severity)?.name,
            ),
        ];

        return { rowOne, rowTwo };
    };

    const { rowOne, rowTwo } = buildDetailRows();

    return (
        <>
            {incidentId && (
                <MarkAsResolvedDialog
                    incidentId={incidentId}
                    isOpen={isMarkAsResolvedDialogOpen}
                    onClose={() => setIsMarkAsResolvedDialogOpen(false)}
                />
            )}
            {incident && editingField && (
                <EditIncidentFieldModal
                    field={editingField}
                    fieldLabel={FIELD_LABELS[editingField]}
                    incident={incident}
                    isOpen={!!editingField}
                    onClose={() => setEditingField(null)}
                    onSave={handleSave}
                    missingQualityChecks={missingQualityChecks}
                    impactedServiceNames={impactedServiceNames}
                    severityLevels={severityLevels}
                    severityLevelNames={severityLevelNames}
                />
            )}
            <FormHeaderAndContent
                rowOneContentHeader={
                    <>
                        <ActionButtonPageTitle
                            label="View Incidents"
                            title="Incident Details"
                            onClick={() => navigate(AppRoutes.viewIncidents)}
                        />
                        {incident && (
                            <div style={{ marginLeft: 'auto' }}>
                                <ButtonWithDropdownMenu
                                    label="Actions"
                                    menuItems={[
                                        {
                                            text: 'Mark as Resolved',
                                            onItemClick: () => setIsMarkAsResolvedDialogOpen(true),
                                        },
                                    ]}
                                    intent={ButtonWithDropdownMenu.Intent.PRIMARY}
                                />
                            </div>
                        )}
                    </>
                }
                rowTwoContentHeader={
                    incident && (
                        <div className="flex__gap--small">
                            <FormTitleLabelValuePair
                                label={incident.name}
                                value={incident.primaryImpactedServiceName}
                            />
                            <IconButton
                                icon={IconNames.EDIT}
                                title="Edit Name"
                                onClick={() => setEditingField('name')}
                            />
                        </div>
                    )
                }
                mainContent={
                    <FormSectionWithRows
                        sectionTitle="Incident Details"
                        isLoading={isFetching}
                        width="100%"
                        rowOneContent={rowOne}
                        rowTwoContent={rowTwo}
                    />
                }
            />
        </>
    );
};
